import React, { useEffect, useMemo, useState } from 'react';
import { addMonths, differenceInMonths, startOfMonth } from 'date-fns';
import { useNavigate } from 'react-router-dom';

import OptionsGraphComponent from 'components/Graphs/OptionsGraph';
import LinearGraphComponent from 'components/Graphs/LinearGraph';
import PrivateRoute from 'components/Navigation/ProtectedRoute';
import SelectFiltersComponent from 'components/SelectFilters';
import { ITableFilter } from 'components/Table/interfaces';
import { showErrorToast } from 'components/Toast';
import ShadowCard from 'components/ShadowCard';

import { DEFAULT_FILTERS, MONTH_OPTIONS, YEAR_OPTIONS } from 'utils/constants';
import { formatDate } from 'utils/functions';
import GeneralApi from 'utils/generalApi';
import { useAuth } from 'contexts/Auth';

const allFilters: Array<ITableFilter> = [
  {
    key: 'date_range',
    title: 'Rango',
    value: DEFAULT_FILTERS.report_date,
    type: 'date',
  },
  {
    key: 'year',
    title: 'Año',
    value: DEFAULT_FILTERS.year,
    type: 'select',
    list: YEAR_OPTIONS,
  },
  {
    key: 'month',
    title: 'Mes',
    value: DEFAULT_FILTERS.month,
    type: 'select',
    list: MONTH_OPTIONS,
  },
];

const defaultFilters: Array<ITableFilter> = [
  {
    key: 'date_range',
    title: 'Rango',
    value: DEFAULT_FILTERS.report_date,
    type: 'date',
  },
];

const defaultMonthFilters: Array<ITableFilter> = [
  {
    key: 'year',
    title: 'Año',
    value: DEFAULT_FILTERS.year,
    type: 'select',
    list: YEAR_OPTIONS,
  },
  {
    key: 'month',
    title: 'Mes',
    value: DEFAULT_FILTERS.month,
    type: 'select',
    list: MONTH_OPTIONS,
  },
];

export default function GraphsPage() {
  const history = useNavigate();
  const auth = useAuth();

  const [loading, setLoading] = useState(false);
  const [appliedFilters, setAppliedFilters] = useState<Array<ITableFilter>>(defaultFilters);
  const [monthAppliedFilters, setMonthAppliedFilters] =
    useState<Array<ITableFilter>>(defaultMonthFilters);

  const [confirmedAmounts, setConfirmedAmounts] = useState<Array<any>>([]);
  const [finishedAmounts, setFinishedAmounts] = useState<Array<any>>([]);

  const [confirmedPrices, setConfirmedPrices] = useState<Array<any>>([]);
  const [finishedPrices, setFinishedPrices] = useState<Array<any>>([]);

  const [purchaseUsers, setPurchaseUsers] = useState<Array<any>>([]);
  const [saleUsers, setSaleUsers] = useState<Array<any>>([]);

  const generalApi = useMemo(() => new GeneralApi(auth, history), [auth, history]);

  const chartX = useMemo(() => {
    return Array.from(
      {
        length:
          differenceInMonths(
            new Date(appliedFilters?.[0]?.value?.to),
            new Date(appliedFilters?.[0]?.value?.from)
          ) + 1,
      },
      (_, i) =>
        formatDate(
          addMonths(startOfMonth(new Date(appliedFilters?.[0]?.value?.from)), i + 1),
          'MMMM yyyy'
        )
    );
  }, [appliedFilters]);

  const getAmountsGraphs = async (filters?: Array<ITableFilter>) => {
    const result = await generalApi.post(
      '/total_amount',
      filters ? filters?.[0]?.value : appliedFilters?.[0]?.value
    );
    setLoading(false);
    if (!result.success) {
      showErrorToast(result.message);
      return;
    }
    setConfirmedAmounts(
      result.data.confirmed.sort((a: any, b: any) =>
        differenceInMonths(
          new Date(a.created_date.year, a.created_date.month),
          new Date(b.created_date.year, b.created_date.month)
        )
      )
    );
    setFinishedAmounts(
      result.data.paid.sort((a: any, b: any) =>
        differenceInMonths(
          new Date(a.created_date.year, a.created_date.month),
          new Date(b.created_date.year, b.created_date.month)
        )
      )
    );
  };
  const getPriceGraphs = async (filters?: Array<ITableFilter>) => {
    const result = await generalApi.post(
      '/average_change',
      filters ? filters?.[0]?.value : appliedFilters?.[0]?.value
    );
    setLoading(false);
    if (!result.success) {
      showErrorToast(result.message);
      return;
    }
    setConfirmedPrices(
      result.data.confirmed.sort((a: any, b: any) =>
        differenceInMonths(
          new Date(a.created_date.year, a.created_date.month),
          new Date(b.created_date.year, b.created_date.month)
        )
      )
    );
    setFinishedPrices(
      result.data.paid.sort((a: any, b: any) =>
        differenceInMonths(
          new Date(a.created_date.year, a.created_date.month),
          new Date(b.created_date.year, b.created_date.month)
        )
      )
    );
  };
  const getUserGraphs = async (filters?: Array<ITableFilter>) => {
    const result = await generalApi.post('/top_users', {
      yearMonth: `${
        filters ? filters?.[0]?.value?.value : monthAppliedFilters?.[0]?.value?.value
      }-${filters ? filters?.[1]?.value?.value : monthAppliedFilters?.[1]?.value?.value}`,
    });
    setLoading(false);
    if (!result.success) {
      showErrorToast(result.message);
      return;
    }
    setPurchaseUsers(result.data.C);
    setSaleUsers(result.data.V);
  };

  useEffect(() => {
    setLoading(true);
    getAmountsGraphs();
    getPriceGraphs();
    getUserGraphs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [generalApi]);

  return (
    <PrivateRoute>
      <div className="text-[#1B205A]">
        <div className="pb-8 2xl:px-24">
          <div className="mt-6 justify-between space-y-4 px-8 pb-4 sm:flex sm:space-y-0">
            <p className="text-2xl font-bold">Gráficos</p>
          </div>
          <SelectFiltersComponent
            allFilters={allFilters}
            appliedFilters={appliedFilters}
            setAppliedFilters={setAppliedFilters}
            getData={(filters) => {
              setLoading(true);
              getAmountsGraphs(filters);
              getPriceGraphs(filters);
            }}
          />
          <div className="mt-4 mb-8 grid gap-8 md:grid-cols-12">
            <ShadowCard wrapperClassName="md:col-span-6" title="Montos - Operaciones confirmadas">
              <LinearGraphComponent
                data1={{
                  name: 'Compras',
                  data: confirmedAmounts
                    ? confirmedAmounts.map((obj) => (obj.C ? Number(Number(obj.C).toFixed(3)) : 0))
                    : [],
                }}
                data2={{
                  name: 'Ventas',
                  data: confirmedAmounts
                    ? confirmedAmounts.map((obj) => (obj.V ? Number(Number(obj.V).toFixed(3)) : 0))
                    : [],
                }}
                chartX={chartX}
                isLoading={loading}
              />
            </ShadowCard>
            <ShadowCard wrapperClassName="md:col-span-6" title="Montos - Operaciones finalizadas">
              <LinearGraphComponent
                data1={{
                  name: 'Compras',
                  data: finishedAmounts
                    ? finishedAmounts.map((obj) => (obj.C ? Number(Number(obj.C).toFixed(3)) : 0))
                    : [],
                }}
                data2={{
                  name: 'Ventas',
                  data: finishedAmounts
                    ? finishedAmounts.map((obj) => (obj.V ? Number(Number(obj.V).toFixed(3)) : 0))
                    : [],
                }}
                chartX={chartX}
                isLoading={loading}
              />
            </ShadowCard>

            <ShadowCard wrapperClassName="md:col-span-6" title="Precio - Operaciones confirmadas">
              <LinearGraphComponent
                type="line"
                data1={{
                  name: 'Compras',
                  data: confirmedPrices
                    ? confirmedPrices.map((obj) => (obj.C ? Number(Number(obj.C).toFixed(3)) : 0))
                    : [],
                }}
                data2={{
                  name: 'Ventas',
                  data: confirmedPrices
                    ? confirmedPrices.map((obj) => (obj.V ? Number(Number(obj.V).toFixed(3)) : 0))
                    : [],
                }}
                chartX={chartX}
                isLoading={loading}
              />
            </ShadowCard>
            <ShadowCard wrapperClassName="md:col-span-6" title="Precio - Operaciones finalizadas">
              <LinearGraphComponent
                type="line"
                data1={{
                  name: 'Compras',
                  data: finishedPrices
                    ? finishedPrices.map((obj) => (obj.C ? Number(Number(obj.C).toFixed(3)) : 0))
                    : [],
                }}
                data2={{
                  name: 'Ventas',
                  data: finishedPrices
                    ? finishedPrices.map((obj) => (obj.V ? Number(Number(obj.V).toFixed(3)) : 0))
                    : [],
                }}
                chartX={chartX}
                isLoading={loading}
              />
            </ShadowCard>
          </div>
          <SelectFiltersComponent
            allFilters={allFilters}
            appliedFilters={monthAppliedFilters}
            setAppliedFilters={setMonthAppliedFilters}
            getData={(filters) => {
              setLoading(true);
              getUserGraphs(filters);
            }}
          />
          <div className="mt-4 mb-8 grid gap-8 md:grid-cols-12">
            <ShadowCard wrapperClassName="md:col-span-6" title="Usuarios Top - Compras">
              <OptionsGraphComponent
                data={
                  purchaseUsers
                    ? purchaseUsers.reduce(
                        (a, b) => (b.c_am > 0 ? { ...a, [b._id.name]: b.c_am } : a),
                        {}
                      )
                    : {}
                }
                total={purchaseUsers ? purchaseUsers.reduce((a, b) => a + b.c_am, 0) : 0}
                isLoading={loading}
              />
            </ShadowCard>
            <ShadowCard wrapperClassName="md:col-span-6" title="Usuarios Top - Ventas">
              <OptionsGraphComponent
                data={
                  saleUsers
                    ? saleUsers.reduce(
                        (a, b) => (b.c_am > 0 ? { ...a, [b._id.name]: b.c_am } : a),
                        {}
                      )
                    : {}
                }
                total={saleUsers ? saleUsers.reduce((a, b) => a + b.c_am, 0) : 0}
                isLoading={loading}
              />
            </ShadowCard>
          </div>
        </div>
      </div>
    </PrivateRoute>
  );
}
